import dayjs from 'dayjs'
import React, { ComponentProps, useEffect, useMemo, useState } from 'react'

import { Button } from '../button'
import { inputVariants } from '../input'
import { classes } from '../utils'
import RawDatePicker from './RawDatePicker'

export type PickerRange = {
  start: Date
  end: Date
}

type Props = {
  /** The date range to display in the date picker. */
  range: PickerRange
  /** A callback that is called to update the date range. */
  onConfirmRangeChange: (range: PickerRange) => void
  /** Optional classes for the containing div */
  containerClassName?: string
} & Pick<
  ComponentProps<typeof RawDatePicker>,
  'htmlFor' | 'showTimeSelect' | 'maxDate' | 'minDate'
>

/** A component that renders a date picker used for changing a date range.
 *  Internal state is used to track changes to the date range, and a callback
 *  is used to finalize the changes.
 **/
export default function RangeDatePicker({
  range,
  onConfirmRangeChange,
  showTimeSelect,
  containerClassName,
  ...props
}: Props) {
  const [newStart, setNewStart] = useState<Date | null>(range.start)
  const [newEnd, setNewEnd] = useState<Date | null>(range.end)

  const startTime = useMemo(() => getTimeFromDate(newStart), [newStart])
  const endTime = useMemo(() => getTimeFromDate(newEnd), [newEnd])

  const handleChange = (dates: [Date, Date]) => {
    setNewStart(dates[0])
    setNewEnd(dates[1])
  }

  const handleTimeChange = (timeString: string, bound: 'start' | 'end') => {
    const [hours, minutes, seconds] = timeString.split(':')

    const parsedHours = parseInt(hours)
    const parsedMinutes = parseInt(minutes)
    const parsedSeconds = parseInt(seconds)

    const parsedPieces = [parsedHours, parsedMinutes, parsedSeconds]
    if (parsedPieces.some((piece) => isNaN(piece))) {
      return
    }

    const setter = (current: Date | null) => {
      const newDate = current ?? new Date()
      newDate.setHours(parsedHours)
      newDate.setMinutes(parsedMinutes)
      newDate.setSeconds(parsedSeconds)
      return newDate
    }

    if (bound === 'start') {
      setNewStart(setter)
    } else {
      setNewEnd(setter)
    }
  }

  // An additional handler for when the user types in the input field of the datepicker.
  // This will only, potentially, update the state on change events (i.e. when the user types).
  // Click events are handled by the datepicker's onChange handler (see handleChange).
  const handleRawChange = (
    event: React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    if (event.type === 'change') {
      const { value } = event.target
      const [start, end] = (value || '').split('-').map((value) => value.trim())

      // for each bound, if it is a valid date, we want to update our local state.
      if (start && !isNaN(Date.parse(start))) {
        setNewStart(new Date(start))
      }

      if (end && !isNaN(Date.parse(end))) {
        setNewEnd(new Date(end))
      }
    }
  }

  const handleConfirm = () => {
    if (newStart && newEnd) {
      onConfirmRangeChange({ start: newStart, end: newEnd })
    }
  }

  // Whenever the range in our context changes, we want to update our local state.
  useEffect(() => {
    setNewStart(range.start)
    setNewEnd(range.end)
  }, [range])

  return (
    <div
      className={classes(
        'w-full max-w-full md:max-w-[18rem]',
        containerClassName,
      )}
    >
      <RawDatePicker
        selected={newStart}
        onChange={handleChange}
        // If we only provide an onChange handler, the datepicker will not update the values on manual typed changes >:(
        // So, we need to provide an onChangeRaw handler as well.
        onChangeRaw={handleRawChange}
        startDate={newStart}
        endDate={newEnd}
        selectsRange
        customInput={<CustomInput />}
        calendarContainer={(props) => (
          <CalendarContainer
            {...props}
            showTimeSelect={showTimeSelect}
            startTime={startTime}
            endTime={endTime}
            onTimeChange={handleTimeChange}
            onConfirm={handleConfirm}
          />
        )}
        shouldCloseOnSelect={false}
        className="h-full border-none"
        dateFormat="MM/d/yyyy, HH:mm:ss"
        wrapperClassName="flex customInput h-full"
        {...props}
      />
    </div>
  )
}

// NOTE: I am use a custom input here because there is simply too much I would have to override our RawTextBox to get it to look like this.
const CustomInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>((props, ref) => (
  <input
    ref={ref}
    {...props}
    data-testid="rangeDatePickerCustomInput"
    className={classes(
      inputVariants(),
      'w-full flex-1 truncate border-none bg-transparent text-sm',
    )}
    data-1p-ignore
  />
))
CustomInput.displayName = 'CustomInput'

type ContainerProps = {
  showTimeSelect?: boolean
  children?: React.ReactNode
  startTime?: string
  endTime?: string
  onTimeChange: (time: string, bound: 'start' | 'end') => void
  onConfirm: () => void
}
const CalendarContainer = ({
  showTimeSelect,
  children,
  startTime,
  endTime,
  onTimeChange,
  onConfirm,
}: ContainerProps) => {
  // NOTE: this duplicated state is really unfortunate, but it seems to be lingering issues from
  // the same bug in the calendar library
  const [localStartTime, setLocalStartTime] = useState(startTime)
  const [localEndTime, setLocalEndTime] = useState(endTime)

  useEffect(() => {
    setLocalStartTime(startTime)
  }, [startTime])

  useEffect(() => {
    setLocalEndTime(endTime)
  }, [endTime])

  const handleTimeChange = (time: string, bound: 'start' | 'end') => {
    if (bound === 'start') {
      setLocalStartTime(time)
    } else {
      setLocalEndTime(time)
    }

    onTimeChange(time, bound)
  }

  return (
    <div className="react-datepicker dittoCalendar relative flex w-[250px] flex-col overflow-hidden rounded-xl border border-border-normal font-sans shadow-lg outline-none">
      <div className="flex w-full justify-center p-3">{children}</div>

      {showTimeSelect && (
        <div className="flex w-full flex-col items-center border-t border-border-normal p-3 text-base">
          <div className="flex w-full">
            <div className="flex w-12 shrink-0 items-center justify-start text-foreground-normal">
              From
            </div>

            <input
              className={classes(
                inputVariants(),
                'flex w-full border-none bg-transparent text-right text-base text-foreground-normal outline-none',
              )}
              type="time"
              step="1"
              value={localStartTime}
              onChange={({ target: { value } }) =>
                handleTimeChange(value, 'start')
              }
              data-testid="rangeDatePickerCustomStartTimeInput"
            />
          </div>

          <div className="flex w-full">
            <div className="flex w-12 shrink-0 items-center justify-start text-foreground-normal">
              To
            </div>

            <input
              className={classes(
                inputVariants(),
                'flex w-full border-none bg-transparent text-right text-base text-foreground-normal outline-none',
              )}
              type="time"
              step="1"
              value={localEndTime}
              onChange={({ target: { value } }) =>
                handleTimeChange(value, 'end')
              }
              data-testid="rangeDatePickerCustomEndTimeInput"
            />
          </div>
        </div>
      )}

      <div className="flex w-full justify-center px-3 pb-3">
        <Button
          variant="primary"
          onClick={onConfirm}
          data-testid="rangeDatePickerCustomConfirm"
          className="w-full"
        >
          Confirm
        </Button>
      </div>
    </div>
  )
}

const getTimeFromDate = (date: Date | null) => {
  if (date) {
    return dayjs(date).format('HH:mm:ss')
  } else {
    return undefined
  }
}
